import { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle } from "lucide-react";

const WA_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || "917889263340";

const inputCls = "w-full bg-[#0d0d10] border border-white/10 focus:border-[#ccff00] outline-none px-4 py-3.5 text-base text-white placeholder:text-zinc-600 transition-colors";
const labelCls = "block font-mono-alt text-[11px] tracking-[0.25em] uppercase text-zinc-500 mb-2.5";

export const OneRmCalc = () => {
  const [weight, setWeight] = useState("");
  const [reps, setReps] = useState("");
  const w = parseFloat(weight);
  const r = parseInt(reps, 10);
  const valid = w > 0 && r >= 1 && r <= 12;
  const orm = valid ? Math.round(w * (1 + r / 30)) : null;

  return (
    <div className="h-full border border-white/10 bg-[#121216] p-8 sm:p-10 hover:border-[#ccff00]/40 transition-colors" data-testid="calc-1rm">
      <p className="font-mono-alt text-[11px] tracking-[0.3em] uppercase text-[#ccff00] mb-2">Strength</p>
      <h3 className="font-display uppercase text-2xl sm:text-3xl mb-8">Calculate your 1RM</h3>
      <div className="grid grid-cols-2 gap-4 mb-8">
        <div>
          <label className={labelCls}>Weight lifted (kg)</label>
          <input data-testid="calc-1rm-weight" type="number" className={inputCls} value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="100" />
        </div>
        <div>
          <label className={labelCls}>Reps (max 12)</label>
          <input data-testid="calc-1rm-reps" type="number" className={inputCls} value={reps} onChange={(e) => setReps(e.target.value)} placeholder="5" />
        </div>
      </div>
      {orm ? (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="calc-1rm-result">
          <p className="font-mono-alt text-[10px] tracking-[0.25em] uppercase text-zinc-500">Estimated one rep max</p>
          <p className="font-display text-6xl sm:text-7xl text-[#ccff00] leading-none my-3">{orm}<span className="text-2xl text-white ml-2">KG</span></p>
          <div className="grid grid-cols-4 gap-2 mt-6">
            {[95, 90, 85, 80].map((p) => (
              <div key={p} className="border border-white/10 bg-[#0d0d10] px-2 py-3 text-center">
                <p className="font-mono-alt text-[9px] tracking-[0.2em] text-zinc-500">{p}%</p>
                <p className="font-display text-lg text-white mt-1">{Math.round(orm * p / 100)}</p>
              </div>
            ))}
          </div>
          <p className="mt-5 text-xs text-zinc-600">Epley formula. Now go earn it under the bar.</p>
        </motion.div>
      ) : (
        <p className="text-sm text-zinc-600" data-testid="calc-1rm-hint">Enter a weight and reps you have actually lifted. Squat, bench, deadlift, anything.</p>
      )}
    </div>
  );
};

const ACTIVITY = [
  { v: "1.2", label: "Desk life" },
  { v: "1.375", label: "1 to 3 sessions a week" },
  { v: "1.55", label: "3 to 5 sessions a week" },
  { v: "1.725", label: "6 to 7 sessions a week" },
  { v: "1.9", label: "Physical job plus training" },
];

export const TdeeCalc = () => {
  const [sex, setSex] = useState("male");
  const [age, setAge] = useState("");
  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");
  const [activity, setActivity] = useState("1.55");

  const a = parseInt(age, 10);
  const h = parseFloat(height);
  const w = parseFloat(weight);
  const valid = a > 10 && a < 100 && h > 100 && h < 250 && w > 30 && w < 300;

  const bmr = valid ? 10 * w + 6.25 * h - 5 * a + (sex === "male" ? 5 : -161) : 0;
  const tdee = valid ? Math.round(bmr * parseFloat(activity)) : 0;
  const cut = Math.round(tdee * 0.8);
  const protein = valid ? Math.round(w * 2) : 0;

  const waMsg = encodeURIComponent(`Hey Nimish, ran your calculator. Maintenance ${tdee} kcal, cut target ${cut} kcal, protein ${protein}g. Build my plan.`);

  return (
    <div className="h-full border border-white/10 bg-[#121216] p-8 sm:p-10 hover:border-[#ccff00]/40 transition-colors" data-testid="calc-tdee">
      <p className="font-mono-alt text-[11px] tracking-[0.3em] uppercase text-[#ccff00] mb-2">Fat loss</p>
      <h3 className="font-display uppercase text-2xl sm:text-3xl mb-8">Deficit calculator</h3>
      <div className="grid grid-cols-2 gap-2 mb-4" data-testid="calc-tdee-sex">
        {["male", "female"].map((s) => (
          <button
            key={s}
            type="button"
            data-testid={`calc-tdee-sex-${s}`}
            onClick={() => setSex(s)}
            className={`py-3 border font-mono-alt text-[10px] tracking-[0.2em] uppercase transition-colors ${
              sex === s ? "border-[#ccff00] bg-[#ccff00]/10 text-white" : "border-white/10 text-zinc-500 hover:text-white"
            }`}
          >
            {s}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div>
          <label className={labelCls}>Age</label>
          <input data-testid="calc-tdee-age" type="number" className={inputCls} value={age} onChange={(e) => setAge(e.target.value)} placeholder="28" />
        </div>
        <div>
          <label className={labelCls}>Height (cm)</label>
          <input data-testid="calc-tdee-height" type="number" className={inputCls} value={height} onChange={(e) => setHeight(e.target.value)} placeholder="175" />
        </div>
        <div>
          <label className={labelCls}>Weight (kg)</label>
          <input data-testid="calc-tdee-weight" type="number" className={inputCls} value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="85" />
        </div>
      </div>
      <label className={labelCls}>Activity</label>
      <select
        data-testid="calc-tdee-activity"
        value={activity}
        onChange={(e) => setActivity(e.target.value)}
        className={`${inputCls} mb-8`}
      >
        {ACTIVITY.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
      </select>
      {valid ? (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="calc-tdee-result">
          <div className="grid grid-cols-3 gap-2">
            <div className="border border-white/10 bg-[#0d0d10] p-4 text-center">
              <p className="font-mono-alt text-[9px] tracking-[0.2em] uppercase text-zinc-500">Maintain</p>
              <p className="font-display text-2xl text-white mt-1">{tdee}</p>
            </div>
            <div className="border border-[#ccff00]/40 bg-[#ccff00]/5 p-4 text-center">
              <p className="font-mono-alt text-[9px] tracking-[0.2em] uppercase text-[#ccff00]">Cut at</p>
              <p className="font-display text-2xl text-[#ccff00] mt-1">{cut}</p>
            </div>
            <div className="border border-white/10 bg-[#0d0d10] p-4 text-center">
              <p className="font-mono-alt text-[9px] tracking-[0.2em] uppercase text-zinc-500">Protein</p>
              <p className="font-display text-2xl text-white mt-1">{protein}g</p>
            </div>
          </div>
          <a
            href={`https://wa.me/${WA_NUMBER}?text=${waMsg}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="calc-tdee-whatsapp-btn"
            className="group mt-6 inline-flex items-center gap-3 border border-white/20 hover:border-[#25d366] hover:text-[#25d366] text-zinc-300 font-mono-alt text-[11px] tracking-[0.2em] uppercase px-6 py-3.5 transition-colors"
          >
            <MessageCircle size={14} />
            Send Nimish my numbers
          </a>
        </motion.div>
      ) : (
        <p className="text-sm text-zinc-600" data-testid="calc-tdee-hint">Fill in your stats. You get maintenance calories, a 20% deficit target and daily protein.</p>
      )}
    </div>
  );
};
